import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import { FiDownload } from "react-icons/fi";
import { CircularProgress } from "@material-ui/core";
import {
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { exportDatasheets } from "../../../../../common/components/Mutation/Datasheets/datasheetMutation";
import {
  errorToastify,
  successToastify,
} from "../../../../../common/utils/Toastify";
import useCustomMutation from "../../../../../common/utils/CustomMutation";

const getModalStyle = (isMobile) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: isMobile ? "90%" : 400,
  maxWidth: "95vw",
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: isMobile ? 2 : 4,
});

const ExportDialog = ({
  specifiedSheet,
  params,
  rowIDs,
  pageSearchText,
  filterModel,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { id } = params;
  const selectedRows = rowIDs;
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [selected, setSelected] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const handleSingleCheckboxChange = (colName) => {
    const newSelected = selected.includes(colName)
      ? selected.filter((col) => col !== colName)
      : [...selected, colName];
    setSelected(newSelected);
    setSelectAll(newSelected.length === specifiedSheet?.columns?.length);
  };

  const handleSelectAllChange = () => {
    if (selectAll) {
      setSelected([]);
    } else {
      setSelected(specifiedSheet?.columns?.map((col) => col.name) || []);
    }
    setSelectAll(!selectAll);
  };

  const onSuccess = (data) => {
    const blob = new Blob([data.data.data.csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${data.data.data.name}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    successToastify("Data exported successfully");
    setExportLoading(false);
    handleClose();
  };

  const onError = () => {
    errorToastify("An error occurred. Please try again.");
  };

  const handleExport = () => {
    if (!selected.length) {
      errorToastify("Select column(s) to export");
      return;
    }
    setExportLoading(true);
    exportDatasheetsAPI({
      id,
      fieldsToReturn: selected,
      selectedRows,
      pageSearchText,
      filterModel,
    });
  };

  const { mutate: exportDatasheetsAPI } = useCustomMutation({
    apiFunc: exportDatasheets,
    onSuccess,
    onError,
    retries: 0,
  });

  return (
    <div>
      <Button
        className="filter-btn-variant"
        onClick={handleOpen}
        endIcon={<FiDownload />}
      >
        Export
      </Button>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="export-modal-title"
        aria-describedby="export-modal-description"
      >
        <Box sx={getModalStyle(isMobile)}>
          <Typography id="export-modal-title" variant="h6" component="h2">
            Select which column(s) to export
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={selectAll}
                onChange={handleSelectAllChange}
                indeterminate={
                  selected.length > 0 &&
                  selected.length < (specifiedSheet?.columns?.length || 0)
                }
              />
            }
            label="Select All"
            sx={{ mt: 1 }}
          />

          <List
            sx={{ maxHeight: isMobile ? "50vh" : "60vh", overflow: "auto" }}
          >
            {specifiedSheet?.columns?.map((column, index) => (
              <ListItem
                key={`${column.id || column.name}-${index}`}
                dense
                sx={{
                  px: 0,
                  "&:hover": { backgroundColor: "action.hover" },
                }}
              >
                <ListItemIcon sx={{ minWidth: "auto", mr: 1 }}>
                  <Checkbox
                    edge="start"
                    checked={selected.includes(column?.name)}
                    onChange={() => handleSingleCheckboxChange(column?.name)}
                    inputProps={{
                      "aria-labelledby": `checkbox-label-${column?.id}`,
                    }}
                    data-testid="check-items"
                  />
                </ListItemIcon>
                <ListItemText
                  id={`checkbox-label-${column.id}`}
                  primary={column.name}
                />
              </ListItem>
            ))}
          </List>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleExport}
              endIcon={exportLoading ? "" : <FiDownload />}
              disabled={!selected.length}
              data-testid="export-button"
            >
              {exportLoading ? (
                <CircularProgress size={24} color="gray" />
              ) : (
                "Export"
              )}
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default ExportDialog;
